import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import DOMPurify from 'dompurify';
import Seo from '../../components/Seo';
import Toast from '../../components/Toast';
import { useAuth } from '../../context/AuthContext';
import { getBlogBySlug } from '../../services/blogs';
import './BlogDetail.css';

function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
}

function ReadingProgressBar({ targetRef }) {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const el = targetRef.current;
      if (!el) return setProgress(0);
      const rect = el.getBoundingClientRect();
      const total = el.offsetHeight - window.innerHeight;
      if (total <= 0) return setProgress(rect.top < 0 ? 100 : 0);
      const scrolled = Math.min(Math.max(-rect.top, 0), total);
      setProgress((scrolled / total) * 100);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [targetRef]);
  return (
    <div className="reading-progress-track" aria-hidden>
      <div className="reading-progress-fill" style={{ width: `${progress}%` }} />
    </div>
  );
}

function IconTwitter() {
  return (
    <svg className="blog-share-icon" viewBox="0 0 24 24" aria-hidden>
      <path fill="currentColor" d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function IconLinkedIn() {
  return (
    <svg className="blog-share-icon" viewBox="0 0 24 24" aria-hidden>
      <path fill="currentColor" d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

function IconWhatsApp() {
  return (
    <svg className="blog-share-icon" viewBox="0 0 24 24" aria-hidden>
      <path fill="currentColor" d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.881 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

function IconLink() {
  return (
    <svg className="blog-share-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M10 13a5 5 0 0 1 0-7l1-1a5 5 0 0 1 7 7l-1 1M14 11a5 5 0 0 1 0 7l-1 1a5 5 0 0 1-7-7l1-1" />
    </svg>
  );
}

function ShareBar({ title }) {
  const [toast, setToast] = useState(null);
  const url = typeof window !== 'undefined' ? window.location.href : '';
  const enc = encodeURIComponent;
  const items = [
    {
      key: 'tw',
      label: 'X',
      href: `https://twitter.com/intent/tweet?text=${enc(title)}&url=${enc(url)}`,
      icon: <IconTwitter />
    },
    {
      key: 'ln',
      label: 'LinkedIn',
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`,
      icon: <IconLinkedIn />
    },
    {
      key: 'wa',
      label: 'WhatsApp',
      href: `https://api.whatsapp.com/send?text=${enc(`${title} ${url}`)}`,
      icon: <IconWhatsApp />
    }
  ];

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setToast({ message: 'Link copied to clipboard.', type: 'success' });
    } catch (_e) {
      setToast({
        message: 'We could not copy automatically. Copy the address from your browser’s address bar.',
        type: 'info'
      });
    }
  };

  return (
    <>
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
      <div className="blog-share">
        <span className="blog-share-label">Share</span>
        <div className="blog-share-row">
          {items.map((s) => (
            <a
              key={s.key}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              className="blog-share-chip"
              aria-label={`Share on ${s.label}`}
              title={s.label}
            >
              {s.icon}
            </a>
          ))}
          <button type="button" className="blog-share-chip blog-share-chip--copy" onClick={copyLink} title="Copy link" aria-label="Copy link">
            <IconLink />
          </button>
        </div>
      </div>
    </>
  );
}

function LockedBanner({ blog, location }) {
  const reason = blog.lock_reason;
  if (reason === 'auth_required') {
    return (
      <div className="blog-lock">
        <div className="blog-lock-icon" aria-hidden>🔒</div>
        <h3>Sign in to continue reading</h3>
        <p>This article is available to logged-in members only. It’s free to join.</p>
        <div className="blog-lock-actions">
          <Link to={`/signin?next=${encodeURIComponent(location.pathname)}`} className="btn-primary">
            Sign in
          </Link>
          <Link to="/signup" className="btn-outline">Create account</Link>
        </div>
      </div>
    );
  }
  if (reason === 'subscription_required') {
    return (
      <div className="blog-lock">
        <div className="blog-lock-icon" aria-hidden>🎓</div>
        <h3>Premium learning content</h3>
        <p>
          Unlock this article by subscribing to one of our courses
          {blog.required_courses && blog.required_courses.length > 0
            ? `: ${blog.required_courses.join(', ')}`
            : '.'}
        </p>
        <div className="blog-lock-actions">
          <Link to="/courses" className="btn-primary">View courses</Link>
        </div>
      </div>
    );
  }
  return (
    <div className="blog-lock">
      <h3>This content is not available.</h3>
    </div>
  );
}

function RelatedCard({ blog }) {
  return (
    <Link to={`/blog/${blog.slug}`} className="related-card">
      <div className="related-card-media">
        {blog.cover_image ? (
          <img src={blog.cover_image} alt={blog.title} loading="lazy" />
        ) : (
          <div className="related-placeholder" aria-hidden>
            {(blog.category || 'Blog')[0]}
          </div>
        )}
      </div>
      <div className="related-card-body">
        {blog.category && <span className="related-category">{blog.category}</span>}
        <div className="related-title">{blog.title}</div>
        <div className="related-meta">
          {formatDate(blog.published_at)}
          {blog.read_minutes ? ` · ${blog.read_minutes} min` : ''}
        </div>
      </div>
    </Link>
  );
}

const BlogDetail = () => {
  const { slug } = useParams();
  const location = useLocation();
  const { token } = useAuth();
  const [blog, setBlog] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const articleRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        setBlog(null);
        const res = await getBlogBySlug(slug, token);
        if (cancelled) return;
        if (res.data?.success) {
          setBlog(res.data.data);
          setRelated(res.data.related || []);
          window.scrollTo({ top: 0, behavior: 'instant' });
        } else {
          setError(res.data?.message || 'Blog not found');
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.status === 404 ? 'Blog not found' : 'Failed to load blog');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, token]);

  const safeHtml = useMemo(() => {
    if (!blog || blog.locked || !blog.content_html) return '';
    return DOMPurify.sanitize(blog.content_html, { ADD_ATTR: ['target', 'rel'] });
  }, [blog]);

  const seoTitle = blog?.seo?.meta_title || blog?.title || 'Blog';
  const seoDesc =
    blog?.seo?.meta_description ||
    blog?.excerpt ||
    'Read the latest from InvFly — practical trading and finance guides for Indian markets.';

  if (loading) {
    return (
      <div className="blog-detail-page">
        <Seo title="Loading…" description="" noIndex />
        <div className="blog-detail-loading">
          <div className="blog-spinner" />
        </div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="blog-detail-page">
        <Seo title="Not found" description="The article you're looking for is not available." noIndex />
        <div className="container">
          <div className="blog-detail-error">
            <h2>{error || 'Blog not found'}</h2>
            <Link to="/blog" className="btn-primary">Back to blog</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-detail-page">
      <Seo title={seoTitle} description={seoDesc} />
      <ReadingProgressBar targetRef={articleRef} />

      <article ref={articleRef} className="blog-article">
        <header className="blog-article-header">
          <div className="container container-narrow">
            <div className="breadcrumbs">
              <Link to="/">Home</Link>
              <span aria-hidden>/</span>
              <Link to="/blog">Blog</Link>
              {blog.category && (
                <>
                  <span aria-hidden>/</span>
                  <Link to={`/blog?category=${encodeURIComponent(blog.category)}`}>
                    {blog.category}
                  </Link>
                </>
              )}
            </div>
            {blog.category && (
              <span className="blog-detail-category">{blog.category}</span>
            )}
            <h1 className="blog-detail-title">{blog.title}</h1>
            {blog.excerpt && <p className="blog-detail-excerpt">{blog.excerpt}</p>}
            <div className="blog-detail-meta">
              {[
                blog.author_name ? `By ${blog.author_name}` : null,
                blog.published_at ? formatDate(blog.published_at) : null,
                blog.read_minutes ? `${blog.read_minutes} min read` : null
              ]
                .filter(Boolean)
                .join(' · ')}
            </div>
          </div>
        </header>

        {blog.cover_image && (
          <div className="container container-wide">
            <div className="blog-cover">
              <img src={blog.cover_image} alt={blog.cover_image_alt || blog.title} />
            </div>
          </div>
        )}

        <div className="container container-narrow">
          {blog.locked ? (
            <LockedBanner blog={blog} location={location} />
          ) : (
            <>
              <div
                className="blog-prose"
                dangerouslySetInnerHTML={{ __html: safeHtml }}
              />
              <ShareBar title={blog.title} />
              {blog.tags && blog.tags.length > 0 && (
                <div className="blog-tags">
                  {blog.tags.map((t) => (
                    <Link key={t} to={`/blog?tag=${encodeURIComponent(t)}`} className="blog-tag">
                      #{t}
                    </Link>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </article>

      {related.length > 0 && (
        <section className="related-section">
          <div className="container">
            <h2 className="related-heading">Continue reading</h2>
            <div className="related-grid">
              {related.map((r) => (
                <RelatedCard key={r.id || r.slug} blog={r} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogDetail;
